import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Stack,
  Pagination,
  TableSortLabel,
  Typography,
  useTheme
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useGetPurchaseOrderList } from "../../../hooks/api/usePurchaseOrder";
import NoPurchaseOrderFound from "./NoPurchaseOrderFound";
import {
  BRAND_COLOR,
  PAGE_PORTAL_ORDERS_PURCHASE_DETAIL,
  PORTAL_PREFIX,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { PurchaseOrder } from "../types/purchaseOrder";
import AddPurchaseOrderModal from "./AddPurchaseOrderModal";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getOrderStatusTextColor } from "../utils/orderUtils";
import { useFormatCurrency } from "../../../hooks/useFormatCurrency";
import { FALLBACK_CURRENCY_CODE } from "../../../constants/currency";
import ExportData from "../../common/ExportData/ExportData";
import { exportPurchaseOrderColumns } from "../constants/exportColumns";
import ListPageToolbar from "../../common/ListPageToolbar";
import BrandButton from "../../common/BrandButton/BrandButton";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import type { SortOrder } from "../interfaces/list";
import { useReportError } from "../../../hooks/useReportError";

const PurchaseOrderList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const formatCurrency = useFormatCurrency();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const { purchaseOrderList } = useSelector(
    (state: RootState) => state.purchaseOrders
  );
  const [openAddModal, setOpenAddModal] = useState(false);
  const useGetPurchaseOrderListMutation = useGetPurchaseOrderList();
  const reportError = useReportError();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const [fetchDone, setFetchDone] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState<SortOrder>("desc");
  const [orderBy, setOrderBy] = useState<keyof PurchaseOrder>("createdAt");
  const itemsPerPage = 10;

  useEffect(() => {
    (async () => {
      try {
        await useGetPurchaseOrderListMutation.mutateAsync({});
      } catch (err) {
        reportError("Failed to fetch purchase order list (UI)", err, {
          component: "PurchaseOrderList"
        });
      } finally {
        setFetchDone(true);
      }
    })();
  }, [reportError]);

  /** Filter by search */
  const filteredList = useMemo(() => {
    if (!search.trim()) return purchaseOrderList;
    return purchaseOrderList.filter(
      (order) =>
        order.documentNumber.toLowerCase().includes(search.toLowerCase()) ||
        order.supplierName?.toLowerCase().includes(search.toLowerCase()) ||
        order.status.toLowerCase().includes(search.toLowerCase())
    );
  }, [purchaseOrderList, search]);

  /** Sorting logic */
  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      const aValue = a[orderBy] ?? "";
      const bValue = b[orderBy] ?? "";
      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (aValue instanceof Date && bValue instanceof Date) {
        return order === "asc"
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }
      return order === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredList, order, orderBy]);

  /** Pagination */
  const paginatedList = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return sortedList.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedList, page]);

  const handleSort = (property: keyof PurchaseOrder) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!fetchDone) {
    return (
      <CommonLoader
        loadingMessage={t(
          "purchase-order-list-loading",
          "Loading purchase orders..."
        )}
      />
    );
  }

  if (purchaseOrderList.length === 0) {
    return <NoPurchaseOrderFound />;
  }

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "background.default",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        gap: 3
      }}
    >
      <ListPageToolbar
        title={t("purchase-orders-title", "Purchase Orders")}
        icon={
          <LocalShippingOutlinedIcon
            sx={{ color: BRAND_COLOR, fontSize: 22 }}
          />
        }
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t(
          "purchase-orders-search-placeholder",
          "Search by order number, supplier, or status."
        )}
        isMobile={matchMD}
        data-testid="create-purchase-order-btn"
      >
        <BrandButton variant="primary" onClick={() => setOpenAddModal(true)}>
          {t("purchase-orders-create-button", "Create Purchase Order")}
        </BrandButton>
        <ExportData
          data={sortedList}
          columns={exportPurchaseOrderColumns}
          filename={`purchase_orders_${new Date().toISOString().split("T")[0]}`}
          title={t("purchase-orders-export-title", "Export Purchase Orders")}
          buttonVariant="outlined"
          buttonText={t("purchase-orders-export-button", "Export")}
          formats={["csv", "xlsx", "pdf", "json"]}
          onExportComplete={(format) => {
            console.log(
              `Exported ${sortedList.length} purchase orders as ${format}`
            );
          }}
          onExportError={(error) => {
            reportError("Purchase order list export failed", error, {
              component: "PurchaseOrderList"
            });
          }}
        />
      </ListPageToolbar>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "documentNumber"}
                  direction={orderBy === "documentNumber" ? order : "asc"}
                  onClick={() => handleSort("documentNumber")}
                >
                  {t("purchase-orders-col-order-number", "Order Number")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "supplierName"}
                  direction={orderBy === "supplierName" ? order : "asc"}
                  onClick={() => handleSort("supplierName")}
                >
                  {t("purchase-orders-col-supplier", "Supplier")}
                </TableSortLabel>
              </TableCell>
              {!matchMD && (
                <>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "totalAmount"}
                      direction={orderBy === "totalAmount" ? order : "asc"}
                      onClick={() => handleSort("totalAmount")}
                    >
                      {t("purchase-orders-col-total-amount", "Total Amount")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "expectedDeliveryDate"}
                      direction={
                        orderBy === "expectedDeliveryDate" ? order : "asc"
                      }
                      onClick={() => handleSort("expectedDeliveryDate")}
                    >
                      {t(
                        "purchase-orders-col-expected-delivery",
                        "Expected Delivery"
                      )}
                    </TableSortLabel>
                  </TableCell>
                </>
              )}
              <TableCell>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleSort("status")}
                >
                  {t("purchase-orders-col-status", "Status")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={() => handleSort("createdAt")}
                >
                  {t("purchase-orders-col-created", "Created")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                {t("purchase-orders-col-actions", "Actions")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedList.map((purchaseOrder) => (
              <TableRow key={purchaseOrder.id} hover>
                <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                  {purchaseOrder.documentNumber}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {purchaseOrder.supplierName ||
                    t("purchase-orders-not-available", "N/A")}
                </TableCell>
                {!matchMD && (
                  <>
                    <TableCell sx={{ fontSize: 13 }}>
                      {formatCurrency(
                        purchaseOrder.totalAmount,
                        purchaseOrder.currency || FALLBACK_CURRENCY_CODE
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {purchaseOrder.expectedDeliveryDate
                        ? formatDate(purchaseOrder.expectedDeliveryDate)
                        : "-"}
                    </TableCell>
                  </>
                )}
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      color: getOrderStatusTextColor(purchaseOrder.status),
                      fontWeight: 500,
                      fontSize: 13
                    }}
                  >
                    {purchaseOrder.status}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {formatDate(purchaseOrder.createdAt)}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 13 }}>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      const route = PAGE_PORTAL_ORDERS_PURCHASE_DETAIL.replace(
                        ":orderId",
                        purchaseOrder.id
                      );
                      navigate(
                        localizedPath(
                          `/${PORTAL_PREFIX}/${route}`,
                          language,
                          defaultLang
                        )
                      );
                    }}
                  >
                    <VisibilityOutlinedIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {sortedList.length > itemsPerPage && (
        <Stack spacing={2} alignItems="center" mt={3}>
          <Pagination
            count={Math.ceil(sortedList.length / itemsPerPage)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Stack>
      )}

      {/* Add Purchase Order Modal */}
      <AddPurchaseOrderModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
      />
    </Box>
  );
};

export default PurchaseOrderList;
